/** Admin filters for the management {@link DataTable}. */
import * as React from 'react';
import { Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/forms/Button';
import { SegmentedControl } from '@/components/forms/SegmentedControl';
import { Autocomplete } from '@/components/forms/Autocomplete';
import { MultiSelect } from '@/components/forms/MultiSelect';
import { DateRangeInput } from '@/components/forms/DateRangeInput';
import { useDepartments } from '@/hooks/useDepartments';
import { useEstablishmentsAutocomplete } from '@/hooks/useEstablishmentsAutocomplete';
import { useProgramsForMode } from '@/hooks/useProgramsForMode';
import { getErrorMessage } from '@/utils/errors-utils';
import { DATE_RANGE_PERIOD_OPTIONS, STAFF_STATUS_CONFIG, type AdminFilters } from './admin-configs';

/** Props for {@link AdminFiltersContent}. */
interface AdminFiltersContentProps {
  filters: AdminFilters;
  allowedFilterIds: string[];
  visibleFilterIds: string[];
  onToggleStatus: (status: string) => void;
  onSetEstablishment: (id: string | undefined) => void;
  onSetProgramIds: (ids: string[] | undefined) => void;
  onSetDepartmentCodes: (codes: string[] | undefined) => void;
  onSetSearchQuery: (query: string) => void;
  onSetDateRange: (range: AdminFilters['dateRange']) => void;
  onSetCustomDateRange: (from: Date | undefined, to: Date | undefined) => void;
}

/** Filter group is allowed by RBAC and currently toggled on in the configure panel. */
function isVisible(
  filterId: string,
  allowedFilterIds: string[],
  visibleFilterIds: string[],
): boolean {
  return allowedFilterIds.includes(filterId) && visibleFilterIds.includes(filterId);
}

/** Staff status chip filter. */
const StatusFilter: React.FC<{
  filters: AdminFilters;
  onToggleStatus: (status: string) => void;
}> = ({ filters, onToggleStatus }) => (
  <div className="filter-group">
    <span className="filter-group-label">Statut</span>
    <div className="filter-options">
      {Object.entries(STAFF_STATUS_CONFIG).map(([key, config]) => {
        const Icon = config.icon;
        const isActive = filters.statuses.includes(key);
        return (
          <Button
            key={key}
            variant="custom"
            size="sm"
            icon={Icon}
            iconPosition="left"
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-all',
              isActive ? 'filter-badge-active' : 'filter-badge-inactive',
            )}
            style={
              {
                '--filter-color': config.badgeColor ?? 'rgb(var(--status-success))',
              } as React.CSSProperties
            }
            onClick={() => onToggleStatus(key)}
          >
            {config.label}
          </Button>
        );
      })}
    </div>
  </div>
);

/** Establishment autocomplete filter. */
const EstablishmentFilter: React.FC<{
  filters: AdminFilters;
  onSetEstablishment: (id: string | undefined) => void;
}> = ({ filters, onSetEstablishment }) => {
  const { options, isLoading, onSearch, onLoadMore, hasMore } = useEstablishmentsAutocomplete();

  return (
    <div className="filter-group">
      <span className="filter-group-label">Établissement</span>
      <div className="w-64">
        <Autocomplete
          placeholder="Rechercher un établissement..."
          options={options}
          value={filters.establishmentId ?? ''}
          onChange={(val) => onSetEstablishment(val || undefined)}
          onSearch={onSearch}
          onLoadMore={onLoadMore}
          hasMore={hasMore}
          isLoading={isLoading}
          minSearchLength={2}
          inputSize="sm"
        />
      </div>
    </div>
  );
};

/** Program {@link MultiSelect} filter. */
const ProgramFilter: React.FC<{
  filters: AdminFilters;
  onSetProgramIds: (ids: string[] | undefined) => void;
}> = ({ filters, onSetProgramIds }) => {
  const { data: programsData } = useProgramsForMode();

  const options = React.useMemo(() => {
    const seen = new Set<string>();
    return (programsData?.data ?? []).flatMap((program) => {
      const id = program.id?.trim();
      if (!id || seen.has(id)) return [];
      seen.add(id);
      return [{ value: id, label: program.name ?? program.label ?? program.code ?? id }];
    });
  }, [programsData]);

  function handleProgramIdsChange(values: string[]): void {
    onSetProgramIds(values.length > 0 ? values : undefined);
  }

  return (
    <div className="filter-group">
      <span className="filter-group-label">Programme</span>
      <div className="w-64">
        <MultiSelect
          placeholder="Tous les programmes"
          inputSize="sm"
          value={filters.programIds ?? []}
          onChange={handleProgramIdsChange}
          options={options}
          maxVisibleTags={2}
        />
      </div>
    </div>
  );
};

/** Department {@link MultiSelect} filter. */
const DepartmentFilter: React.FC<{
  filters: AdminFilters;
  onSetDepartmentCodes: (codes: string[] | undefined) => void;
}> = ({ filters, onSetDepartmentCodes }) => {
  const { data: departments = [], error: departmentsError } = useDepartments('FR');

  const options = React.useMemo(
    () => departments.map((dept) => ({ value: dept.code, label: dept.name })),
    [departments],
  );

  function handleDepartmentCodesChange(values: string[]): void {
    onSetDepartmentCodes(values.length > 0 ? values : undefined);
  }

  return (
    <div className="filter-group">
      <span className="filter-group-label">Département</span>
      <div className="w-64">
        <MultiSelect
          placeholder="Sélectionner des départements..."
          options={options}
          value={filters.departmentCodes ?? []}
          onChange={handleDepartmentCodesChange}
          inputSize="sm"
          error={departmentsError ? getErrorMessage(departmentsError) : undefined}
        />
      </div>
    </div>
  );
};

/** Debounced name/email/phone search. */
const SearchFilter: React.FC<{
  filters: AdminFilters;
  onSetSearchQuery: (query: string) => void;
}> = ({ filters, onSetSearchQuery }) => {
  const [localValue, setLocalValue] = React.useState(filters.searchQuery ?? '');
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => {
    setLocalValue(filters.searchQuery ?? '');
  }, [filters.searchQuery]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setLocalValue(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onSetSearchQuery(val.trim());
    }, 300);
  };

  React.useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return (
    <div className="filter-group">
      <span className="filter-group-label">Recherche</span>
      <div className="relative w-64">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <input
          type="text"
          value={localValue}
          onChange={handleChange}
          placeholder="Nom, prénom, email, téléphone..."
          className="w-full h-9 pl-10 pr-3 rounded-lg border border-[color-mix(in_srgb,var(--color-1-nuance-3)_50%,transparent)] bg-[rgb(var(--card-rgb)/0.3)] text-sm focus:outline-none focus:border-[var(--color-2-nuance-1)]"
        />
      </div>
    </div>
  );
};

/** Creation date range filter. */
const CreationDateFilter: React.FC<{
  filters: AdminFilters;
  onSetDateRange: (range: AdminFilters['dateRange']) => void;
  onSetCustomDateRange: (from: Date | undefined, to: Date | undefined) => void;
}> = ({ filters, onSetDateRange, onSetCustomDateRange }) => {
  const toDateString = (d?: Date) => {
    if (!d) return undefined;
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const fromStr = toDateString(filters.customDateFrom);
  const toStr = toDateString(filters.customDateTo);

  const handleFromChange = (val: string) => {
    if (!val) {
      onSetCustomDateRange(undefined, filters.customDateTo);
      return;
    }
    const [year, month, day] = val.split('-').map(Number);
    const d = new Date(year, month - 1, day, 0, 0, 0);
    onSetCustomDateRange(d, filters.customDateTo);
    onSetDateRange('all');
  };

  const handleToChange = (val: string) => {
    if (!val) {
      onSetCustomDateRange(filters.customDateFrom, undefined);
      return;
    }
    const [year, month, day] = val.split('-').map(Number);
    const d = new Date(year, month - 1, day, 23, 59, 59);
    onSetCustomDateRange(filters.customDateFrom, d);
    onSetDateRange('all');
  };

  return (
    <div className="filter-group">
      <span className="filter-group-label">Date de création</span>
      <div className="flex items-center gap-2">
        <SegmentedControl
          size="sm"
          value={filters.customDateFrom ? '' : (filters.dateRange ?? 'all')}
          onChange={(v) => {
            onSetDateRange(v as AdminFilters['dateRange']);
            onSetCustomDateRange(undefined, undefined);
          }}
          options={[...DATE_RANGE_PERIOD_OPTIONS]}
        />
        {filters.dateRange === 'all' && (
          <div className="w-auto">
            <DateRangeInput
              startDate={fromStr}
              endDate={toStr}
              onStartDateChange={handleFromChange}
              onEndDateChange={handleToChange}
              layout="horizontal"
              showConnector={true}
              showPresets={false}
              hidePickerLabels={true}
              compact={true}
            />
          </div>
        )}
      </div>
    </div>
  );
};

/** Admin filters for the management {@link DataTable}. */
export const AdminFiltersContent: React.FC<AdminFiltersContentProps> = ({
  filters,
  allowedFilterIds,
  visibleFilterIds,
  onToggleStatus,
  onSetEstablishment,
  onSetProgramIds,
  onSetDepartmentCodes,
  onSetSearchQuery,
  onSetDateRange,
  onSetCustomDateRange,
}) => {
  const show = (id: string) => isVisible(id, allowedFilterIds, visibleFilterIds);

  return (
    <div className="filter-groups-container">
      {show('filter-status') && <StatusFilter filters={filters} onToggleStatus={onToggleStatus} />}
      {show('filter-establishment') && (
        <EstablishmentFilter filters={filters} onSetEstablishment={onSetEstablishment} />
      )}
      {show('filter-program') && (
        <ProgramFilter filters={filters} onSetProgramIds={onSetProgramIds} />
      )}
      {show('filter-department') && (
        <DepartmentFilter filters={filters} onSetDepartmentCodes={onSetDepartmentCodes} />
      )}
      {show('filter-search') && (
        <SearchFilter filters={filters} onSetSearchQuery={onSetSearchQuery} />
      )}
      {show('filter-dates') && (
        <CreationDateFilter
          filters={filters}
          onSetDateRange={onSetDateRange}
          onSetCustomDateRange={onSetCustomDateRange}
        />
      )}
    </div>
  );
};
